// src/Components/Dashboard/Dashboard.js

import React, { useState, useEffect, useRef, useMemo } from "react";
import sales from "../Sales";
import Navbar from "../Navbar/Navbar";
import { useNavigate, useParams } from "react-router-dom";
import Cookies from "universal-cookie";
import dayjs from "dayjs";

import "./Dashboard.css";

/**
 * formatCurrency(value)
 *  - Converts a number into a string with two decimals (if needed)
 *    and replaces decimal dot with comma.
 *  - If integer, no decimals.
 */
function formatCurrency(value) {
  if (!value && value !== 0) return "0";
  if (Number.isInteger(value)) {
    return value.toString().replace(".", ",");
  } else {
    return value.toFixed(2).toString().replace(".", ",");
  }
}

// ───────────────────────────────────────────────────────────────────────────
//                    ARC GAUGE + CONFETTI (INLINE)
// ───────────────────────────────────────────────────────────────────────────
//
// Styles staan in Dashboard.css onder “HERO — TEAM TARGET”.

/**
 * heatColor(percent)
 *  - Kleurtrap van rustig amber naar roodgloeiend. Hoe dichter bij target,
 *    hoe heter (en dus dramatischer) de ring brandt.
 */
const HEAT_STOPS = [
  [0.0, [255, 214, 150]],
  [0.35, [255, 178, 74]],
  [0.7, [255, 118, 26]],
  [1.0, [255, 58, 18]],
];

function heatColor(percent, alpha = 1) {
  const p = Math.min(100, Math.max(0, percent)) / 100;
  let i = 0;
  while (i < HEAT_STOPS.length - 2 && p > HEAT_STOPS[i + 1][0]) i += 1;
  const [p0, from] = HEAT_STOPS[i];
  const [p1, to] = HEAT_STOPS[i + 1];
  const t = p1 === p0 ? 0 : (p - p0) / (p1 - p0);
  const rgb = from.map((v, k) => Math.round(v + (to[k] - v) * t));
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
}

/**
 * scatter(i, seed)
 *  - Vaste pseudo-random per index. Geeft de vonken en rookpluimen onderling
 *    verschil zonder dat de boel bij elke render opnieuw door elkaar springt
 *    (Math.random zou dat wel doen).
 */
function scatter(i, seed) {
  const x = Math.sin(i * 12.9898 + seed * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

/**
 * ArcGauge
 *  - De halve ring met het teampercentage. De boog krijgt bewust marge in de
 *    viewBox: eerder liep de gloed tegen de filter- en viewport-rand aan en
 *    dat gaf die harde, blokkerige vlakken in de hoeken.
 *  - Het vuur schaalt kwadratisch mee met het percentage (rustig onderin,
 *    echt heet richting target) en pulseert maar heel traag.
 */
const ArcGauge = ({ percent = 0, size = 400, stroke = 32 }) => {
  // Ruimte rondom de boog zodat de blur nergens wordt afgekapt.
  const padX = 30;
  const padTop = 28;
  const padBottom = 32;

  const centerX = size / 2;
  const radius = centerX - padX - stroke / 2;
  const centerY = padTop + radius + stroke / 2;
  const height = centerY + stroke / 2 + padBottom;

  const arcLength = Math.PI * radius;
  const clamped = Math.min(100, Math.max(0, percent));
  const progress = clamped / 100;
  const dashOffset = arcLength * (1 - progress);
  const dash = `${arcLength} ${arcLength}`;
  const d = `M ${centerX - radius} ${centerY} A ${radius} ${radius} 0 0 1 ${
    centerX + radius
  } ${centerY}`;

  // Kop van de balk — daar zitten het witte puntje en de vonken.
  const angle = Math.PI * (1 - progress);
  const tipX = centerX + radius * Math.cos(angle);
  const tipY = centerY - radius * Math.sin(angle);

  // Het verloop is horizontaal, maar de boog loopt rond. Deze stop zet de
  // heetste kleur exact onder de kop, zodat de kleur de boog volgt in plaats
  // van het vlak. Zonder dit zag je links een bleke vlek.
  const tipStop = Math.max(0.02, (1 - Math.cos(Math.PI * progress)) / 2);

  const base = heatColor(16 + clamped * 0.28);
  const mid = heatColor(clamped * 0.75);
  const hot = heatColor(clamped);

  // Kwadratisch: onder de 40% blijft het rustig, daarboven loopt het op.
  const heat = progress * progress;
  const showFire = progress > 0.02;
  // De halo om de balk blijft bewust ingetogen (20% zachter); het drama moet
  // van de kop komen, niet van een dikke gloed rond de hele ring.
  const bloomOpacity = (0.08 + 0.58 * heat) * 0.8;
  const glowOpacity = (0.3 + 0.28 * progress) * 0.8;
  const bloomBlur = 7 + 7 * progress;
  const glowBlur = 3 + 3 * progress;
  // Vonkjes bij de kop — een beetje branden, niet meer dan dat. Meer en
  // kleinere deeltjes met een bredere spreiding in levensduur: dat is wat een
  // deeltjesvuur rustig laat ogen. Weinig grote vonken op een strak ritme
  // leest als een lopende band, en dat was precies wat er raar uitzag.
  const emberCount = 8 + Math.round(20 * heat);
  const emberDuration = 2.2 - 0.8 * progress;
  const emberReach = 26 + 40 * heat;
  // Lift moet de halve balkdikte (16px) halen, anders verdwijnen de vonken
  // in de gloeiende band zelf; ze scheren nu net langs de buitenkant.
  const emberLift = 18 + 16 * heat;

  // Rook én vonken drijven niet recht omhoog maar naar achteren over het stuk
  // boog dat al gevuld is — alsof de kop zich een weg brandt en een spoor
  // achterlaat. De richting is de raaklijn tegen de looprichting in; "lift"
  // duwt een deeltje radiaal naar buiten en blijft bewust klein, zodat alles
  // laag langs de boog blijft in plaats van in de lucht te hangen.
  const trailX = -Math.sin(angle);
  const trailY = -Math.cos(angle);
  const outX = Math.cos(angle);
  const outY = -Math.sin(angle);
  const smokeCount = 3 + Math.round(5 * heat);
  const smokeDuration = 3.6 - 0.8 * progress;
  const smokeReach = 34 + 44 * heat;
  const smokeLift = 4 + 7 * heat;
  // Rook mag je nauwelijks zien. Op het blauwe hero-vlak werd een donkere
  // pluim een vuile veeg in plaats van rook, dus: warm grijs en dun.
  const smokeOpacity = 0.09 + 0.13 * heat;

  return (
    <svg
      width={size}
      height={height}
      viewBox={`0 0 ${size} ${height}`}
      style={{ overflow: "visible" }}
    >
      <defs>
        <linearGradient id="arcHeat" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={base} />
          <stop offset={tipStop * 0.55} stopColor={mid} />
          <stop offset={tipStop} stopColor={hot} />
          <stop offset="1" stopColor={hot} />
        </linearGradient>

        {/* Ruime filterregio's: krap afgestelde regio's knipten de blur af en
            dat leverde zichtbare rechthoekige randen op. */}
        <filter id="arcBloom" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation={bloomBlur} />
        </filter>
        <filter id="arcGlow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation={glowBlur} />
        </filter>
        <filter id="arcTip" x="-200%" y="-200%" width="500%" height="500%">
          <feGaussianBlur stdDeviation={5 + 6 * progress} />
        </filter>

        {/* Rookpluim: radiale fill die naar de rand wegvalt. Zachter dan een
            blur-filter en veel goedkoper, en dat telt op een scherm dat de
            hele dag aanstaat. */}
        <radialGradient id="arcSmoke">
          <stop offset="0" stopColor="rgba(104, 99, 110, 0.55)" />
          <stop offset="45%" stopColor="rgba(104, 99, 110, 0.24)" />
          <stop offset="100%" stopColor="rgba(104, 99, 110, 0)" />
        </radialGradient>
      </defs>

      {/* Track */}
      <path
        d={d}
        fill="none"
        stroke="rgba(255,255,255,0.16)"
        strokeWidth={stroke}
        strokeLinecap="round"
      />

      {/* Wijde gloed — dit is het "vuur" dat meegroeit met het percentage.
          Zelfde dash als de voortgang, dus de gloed blijft op het gevulde
          stuk hangen en zweeft niet rond de lege track. */}
      {showFire && (
        <path
          className="arc-bloom"
          d={d}
          fill="none"
          stroke="url(#arcHeat)"
          strokeWidth={stroke + 26}
          strokeLinecap="round"
          strokeDasharray={dash}
          strokeDashoffset={dashOffset}
          filter="url(#arcBloom)"
          style={{ "--flame": bloomOpacity }}
        />
      )}

      {/* Strakke gloed vlak om de balk — statisch, die hoeft niet te pulsen */}
      {showFire && (
        <path
          d={d}
          fill="none"
          stroke="url(#arcHeat)"
          strokeWidth={stroke + 8}
          strokeLinecap="round"
          strokeDasharray={dash}
          strokeDashoffset={dashOffset}
          filter="url(#arcGlow)"
          opacity={glowOpacity}
        />
      )}

      {/* Voortgang */}
      <path
        d={d}
        fill="none"
        stroke="url(#arcHeat)"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={dash}
        strokeDashoffset={dashOffset}
        style={{ transition: "stroke-dashoffset 0.9s ease-in-out" }}
      />

      {/* Rooksliert achter de kop aan — staat vóór de kop in de DOM, dus het
          witte puntje blijft er altijd bovenop liggen */}
      {showFire && (
        <g
          className="arc-smoke"
          style={{ transform: `translate(${tipX}px, ${tipY}px)` }}
        >
          {Array.from({ length: smokeCount }, (_, i) => {
            const a = scatter(i, 4);
            const b = scatter(i, 5);
            const reach = smokeReach * (0.7 + 0.7 * a);
            const lift = smokeLift * (0.4 + 1.2 * b);
            return (
              <circle
                key={i}
                className="smoke"
                cx={(a - 0.5) * 8}
                cy={(b - 0.5) * 6}
                r={4.5 + 3 * b}
                fill="url(#arcSmoke)"
                style={{
                  // Negatieve delay: de pluim staat al vol bij de eerste frame
                  // en er loopt geen zichtbare golf door de rij heen.
                  animationDelay: `${-scatter(i, 6) * smokeDuration}s`,
                  animationDuration: `${smokeDuration * (0.85 + 0.4 * a)}s`,
                  "--dx": `${trailX * reach + outX * lift}px`,
                  "--dy": `${trailY * reach + outY * lift}px`,
                  "--puff": 1.6 + 0.6 * b,
                  "--smoke": smokeOpacity,
                }}
              />
            );
          })}
        </g>
      )}

      {/* Kop: hete halo onder een wit puntje */}
      {clamped > 1 && (
        <>
          <circle
            cx={tipX}
            cy={tipY}
            r={stroke * 0.6}
            fill={hot}
            opacity={0.35 + 0.45 * heat}
            filter="url(#arcTip)"
          />
          <circle cx={tipX} cy={tipY} r={stroke * 0.26} fill="#ffffff" />
        </>
      )}

      {/* Vonken schieten met de boog mee naar achteren, niet de lucht in */}
      {showFire && (
        <g
          className="arc-embers"
          style={{ transform: `translate(${tipX}px, ${tipY}px)` }}
        >
          {Array.from({ length: emberCount }, (_, i) => {
            const a = scatter(i, 1);
            const b = scatter(i, 2);
            const c = scatter(i, 3);
            const reach = emberReach * (0.55 + 0.9 * a);
            const lift = emberLift * (0.7 + 1.1 * b);
            const dur = emberDuration * (0.6 + 0.95 * c);
            return (
              <circle
                key={i}
                className="ember"
                cx={(a - 0.5) * 11}
                cy={(b - 0.5) * 8}
                r={0.6 + 1.5 * c}
                fill={heatColor(clamped - Math.round(c * 22))}
                style={{
                  // Negatieve, ongelijke delay. Gelijke stapjes van 1/n gaven
                  // een herkenbaar terugkerend patroon; zo is er geen ritme
                  // meer te horen en staat de sliert meteen vol.
                  animationDelay: `${-scatter(i, 7) * dur}s`,
                  animationDuration: `${dur}s`,
                  "--dx": `${trailX * reach + outX * lift}px`,
                  "--dy": `${trailY * reach + outY * lift}px`,
                  "--spark": 0.65 + 0.35 * a,
                }}
              />
            );
          })}
        </g>
      )}

    </svg>
  );
};

/**
 * Confetti
 *  - Alleen te zien als het team target gehaald is. De stukjes worden één keer
 *    met useMemo bepaald, zodat de animatie niet herstart bij elke klok-tick.
 */
const Confetti = ({ pieces = 60 }) => {
  const bits = useMemo(
    () =>
      Array.from({ length: pieces }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 6,
        duration: 4 + Math.random() * 4,
        size: 6 + Math.random() * 8,
        round: i % 3 === 0,
        color: ["#ffffff", "#ffd166", "#ff9a3c", "#7fd8ff", "#cfeaff"][i % 5],
      })),
    [pieces]
  );

  return (
    <div className="confetti" aria-hidden="true">
      {bits.map((bit, i) => (
        <span
          key={i}
          className={`confetti-bit${bit.round ? " round" : ""}`}
          style={{
            left: `${bit.left}%`,
            width: `${bit.size}px`,
            height: `${bit.size * (bit.round ? 1 : 0.45)}px`,
            background: bit.color,
            animationDelay: `${bit.delay}s`,
            animationDuration: `${bit.duration}s`,
          }}
        />
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//                       KOLOM 1 — VERKOCHTE DIENSTEN
// ─────────────────────────────────────────────────────────────────────────────
// Kolom 2 wisselt af tussen het podium en de rest van de ranglijst: nummer 4
// en lager onder het podium plakken leest verwarrend, los erna is duidelijker.
const PODIUM_VIEW_MS = 30000; // podium staat 30s in beeld
const REST_VIEW_MS = 10000; // daarna 10s "Andere verkopers"

const ROW_GAP_MIN = 18; // px — het krappe ritme waarmee gemeten wordt
const ROW_GAP_MAX = 40; // px — zover mag de tussenruimte oplopen bij weinig rijen
const MARQUEE_SPEED = 32; // px per seconde — rustig genoeg om mee te lezen

/**
 * MostSoldServices
 *  - Staat op moduleniveau en is gememoïseerd, zodat de kaart niet opnieuw
 *    mount bij elke klok-tick; anders zou de marquee elke minuut herstarten.
 *  - Passen alle diensten in de kaart? Dan loopt de tussenruimte mee met de
 *    vrije hoogte, maar nooit verder dan ROW_GAP_MAX. Zo vult een korte lijst
 *    de kaart netjes zonder de canyons die je krijgt als je alle restruimte
 *    over de rijen uitsmeert.
 *  - Zijn er te veel diensten, dan rolt de lijst rond: hij staat twee keer in
 *    de baan en schuift precies één lijsthoogte op, waarna hij weer op zijn
 *    beginpositie staat (naadloze lus).
 */
const MostSoldServices = React.memo(({ soldServices, monthLabel }) => {
  const viewportRef = useRef(null);
  const listRef = useRef(null);
  const [layout, setLayout] = useState({ gap: ROW_GAP_MIN, duration: 0 });

  const maxValue = soldServices.length
    ? Math.max(...soldServices.map((s) => s.totalPrice))
    : 0;

  // Meet de kaart en beslis: uitrekken tot ROW_GAP_MAX, of rollen.
  useEffect(() => {
    const viewport = viewportRef.current;
    const list = listRef.current;
    if (!viewport || !list) return undefined;

    const measure = () => {
      const count = list.children.length;
      if (!count) return;
      // Reken terug naar de hoogte van de rijen zónder tussenruimte. Dat getal
      // verandert niet als we de gap daarna aanpassen, dus de meting blijft
      // stabiel en gaat niet heen en weer klapperen.
      const appliedGap = parseFloat(getComputedStyle(list).rowGap) || 0;
      const rowsHeight = list.scrollHeight - appliedGap * (count - 1);
      const available = viewport.clientHeight;
      const gaps = Math.max(1, count - 1);
      const tight = rowsHeight + ROW_GAP_MIN * gaps;

      const next =
        tight - available > 8
          ? {
              gap: ROW_GAP_MIN,
              duration: Math.max(
                14,
                Math.round((tight + ROW_GAP_MIN) / MARQUEE_SPEED)
              ),
            }
          : {
              gap: Math.min(
                ROW_GAP_MAX,
                Math.max(ROW_GAP_MIN, (available - rowsHeight) / gaps)
              ),
              duration: 0,
            };

      setLayout((prev) =>
        Math.abs(prev.gap - next.gap) < 0.5 && prev.duration === next.duration
          ? prev
          : next
      );
    };

    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    observer.observe(list);
    return () => observer.disconnect();
  }, [soldServices]);

  const isScrolling = layout.duration > 0;

  const rows = soldServices.map((service, index) => (
    <div
      key={index}
      className="service-row animate-fade-in"
      style={{ animationDelay: `${index * 0.06}s` }}
    >
      <div className="nm">{service.name}</div>
      <div className="vl">€{formatCurrency(service.totalPrice)}</div>
      <div className="track">
        <i
          style={{
            width: `${
              maxValue ? Math.max(4, (service.totalPrice / maxValue) * 100) : 0
            }%`,
          }}
        />
      </div>
    </div>
  ));

  return (
    <div className="card">
      <div className="card-head">
        <h3 className="card-title">Verkochte diensten</h3>
        <h3 className="card-title hl">{monthLabel}</h3>
      </div>
      {soldServices.length === 0 ? (
        <div className="empty-note">Nog geen diensten verkocht</div>
      ) : (
        <div
          ref={viewportRef}
          className={`card-scroll${isScrolling ? " is-scrolling" : ""}`}
        >
          <div
            className="card-scroll-track"
            style={{
              rowGap: `${layout.gap}px`,
              animationDuration: isScrolling
                ? `${layout.duration}s`
                : undefined,
            }}
          >
            <div
              className="card-list"
              ref={listRef}
              style={{ rowGap: `${layout.gap}px` }}
            >
              {rows}
            </div>
            {/* Tweede kopie: alleen om de lus naadloos te maken. */}
            {isScrolling && (
              <div
                className="card-list"
                aria-hidden="true"
                style={{ rowGap: `${layout.gap}px` }}
              >
                {rows}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
//                         GLOBAL STATE & CACHING LOGIC
// ─────────────────────────────────────────────────────────────────────────────
let isFetchingGlobal = false;
let fetchAttemptsCount = 0;
const maxFetchAttempts = 3;
let controller = null;
let isMounted = false;

// In‐memory cache (valid for 5 minutes)
const cachedData = {
  teamTarget: {},
  bestSeller: {},
  deals: {},
  existantDeals: {},
  newDeals: {},
  totalPrice: 0,
  amount: 0,
  rankList: [],
  soldServices: [],
  yearRankList: [],
  xMonthRankList: [],
  yMonthRankList: [],
  lastUpdated: null,
  currentMonth: null,
  currentYear: null,
};

/**
 * ResponsiveContainer
 *  - Wraps the 1920×1080 “canvas” and scales it uniformly so no scrollbars appear.
 */
const ResponsiveContainer = ({ children }) => {
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const onResize = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const designWidth = 1920;
  const designHeight = 1080;
  const scaleX = viewport.width / designWidth;
  const scaleY = viewport.height / designHeight;
  const scale = Math.min(scaleX, scaleY);
  const leftoverX = (viewport.width - designWidth * scale) / 2;
  const leftoverY = (viewport.height - designHeight * scale) / 2;

  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        position: "relative",
        backgroundColor: "#0f2f47",
      }}
    >
      <div
        style={{
          width: `${designWidth}px`,
          height: `${designHeight}px`,
          transform: `translate(${leftoverX}px, ${leftoverY}px) scale(${scale})`,
          transformOrigin: "top left",
          position: "absolute",
          top: 0,
          left: 0,
        }}
      >
        {children}
      </div>
    </div>
  );
};

/**
 * ResponsiveDashboard
 *  - Main component: fetches data, automatically retries on error,
 *    and renders the full dashboard with the hero target gauge.
 */
const ResponsiveDashboard = () => {
  const cookies = new Cookies();
  const navigate = useNavigate();
  const { month, year } = useParams(); // e.g. "06" & "2025"

  // Convert "2025-06" → "juni" in Dutch locale
  const now = dayjs(`${year}-${month}`).locale("nl").format("MMMM");
  // Vorige maand, voor het label onder “beste verkoper”
  const previousMonth = dayjs(`${year}-${month}`)
    .subtract(1, "month")
    .locale("nl")
    .format("MMMM");

  // ────────────────────────────────────────────────────────────────────────────
  //                              STATE HOOKS
  // ────────────────────────────────────────────────────────────────────────────
  const [teamTarget, setTeamTarget] = useState({});
  const [bestSeller, setBestSeller] = useState({});
  const [deals, setDeals] = useState({});
  const [existantDeals, setExistantDeals] = useState({});
  const [newDeals, setNewDeals] = useState({});
  const [totalPrice, setTotalPrice] = useState(0);
  const [amount, setAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorReason, setErrorReason] = useState(null);
  const [rankList, setRankList] = useState([]);
  const [soldServices, setSoldServices] = useState([]);
  const [yearRankList, setYearRankList] = useState([]);
  const [xMonthRankList, setXMonthRankList] = useState([]);
  const [yMonthRankList, setYMonthRankList] = useState([]);

  // Klok: HH:mm en een korte datum ("Ma 8 sep").
  const DAY_NAMES = ["zo", "ma", "di", "wo", "do", "vr", "za"];
  const MONTH_NAMES = [
    "jan",
    "feb",
    "mrt",
    "apr",
    "mei",
    "jun",
    "jul",
    "aug",
    "sep",
    "okt",
    "nov",
    "dec",
  ];

  const getCurrentTime = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    const dayName = DAY_NAMES[now.getDay()];
    const date = `${dayName[0].toUpperCase()}${dayName.slice(1)} ${now.getDate()} ${
      MONTH_NAMES[now.getMonth()]
    }`;
    return { hours, minutes, date };
  };
  const [currentTime, setCurrentTime] = useState(getCurrentTime());

  // Welke helft van de ranglijst kolom 2 nu toont. Zonder nummer 4 en lager
  // valt er niets te wisselen en blijft het podium gewoon staan.
  const [rankView, setRankView] = useState("podium");
  const hasRestRanks = rankList.length > 3;

  useEffect(() => {
    if (!hasRestRanks) {
      setRankView("podium");
      return undefined;
    }
    const timer = setTimeout(
      () => setRankView(rankView === "podium" ? "rest" : "podium"),
      rankView === "podium" ? PODIUM_VIEW_MS : REST_VIEW_MS
    );
    return () => clearTimeout(timer);
  }, [hasRestRanks, rankView]);

  // ────────────────────────────────────────────────────────────────────────────
  //                    FETCHING & CACHING WITH AUTO‐RETRY
  // ────────────────────────────────────────────────────────────────────────────

  // Basic wrapper around sales.get, including abort signal.
  const fetchWithTimeout = async (type, endpoint, signal) => {
    try {
      const response = await sales.get(endpoint, { signal });
      // If the HTTP status is not in the 200 range, treat it as an error:
      if (response.status < 200 || response.status >= 300) {
        const msg = `HTTP ${response.status} ${response.statusText}`;
        console.error(`[fetchWithTimeout] ${type} → ${msg}`);
        return {
          type,
          error: true,
          errorMessage: msg,
          statusCode: response.status,
        };
      }
      return { type, data: response.data };
    } catch (error) {
      if (error.name === "AbortError") {
        // We aborted intentionally; just rethrow so the caller can decide what to do:
        throw error;
      }
      // Otherwise, log the actual network/JS error:
      const msg = error.response
        ? `HTTP ${error.response.status} ${error.response.statusText}`
        : error.message || "Unknown network error";
      console.error(`[fetchWithTimeout] ${type} → ${msg}`, error);
      return {
        type,
        error: true,
        errorMessage: msg,
        statusCode: error.response?.status,
      };
    }
  };

  const fetchAll = async (month, year, reload) => {
    if (isFetchingGlobal) return;
    if (fetchAttemptsCount >= maxFetchAttempts) {
      // Too many attempts: set error so we can schedule a retry
      if (isMounted) {
        setHasError(true);
        setIsLoading(false);
      }
      return;
    }

    // Abort any in‐flight request
    if (controller) {
      controller.abort();
    }
    controller = new AbortController();

    try {
      isFetchingGlobal = true;
      if (!reload && isMounted) {
        setIsLoading(true);
        setHasError(false);
        setErrorReason(null);
      }
      fetchAttemptsCount++;

      // Check cache (5 min)
      const nowTs = Date.now();
      if (
        cachedData.lastUpdated &&
        cachedData.currentMonth === month &&
        cachedData.currentYear === year &&
        nowTs - cachedData.lastUpdated < 300000
      ) {
        if (isMounted) {
          setTeamTarget(cachedData.teamTarget);
          setBestSeller(cachedData.bestSeller);
          setDeals(cachedData.deals);
          setExistantDeals(cachedData.existantDeals);
          setNewDeals(cachedData.newDeals);
          setTotalPrice(cachedData.totalPrice);
          setAmount(cachedData.amount);
          setRankList(cachedData.rankList);
          setSoldServices(cachedData.soldServices);
          setYearRankList(cachedData.yearRankList);
          setXMonthRankList(cachedData.xMonthRankList);
          setYMonthRankList(cachedData.yMonthRankList);
          setIsLoading(false);
          setHasError(false);
        }
        isFetchingGlobal = false;
        return;
      }

      // Determine previous month/year
      const monthNumber = parseInt(month, 10);
      const yearNumber = parseInt(year, 10);
      const prevMonth = monthNumber === 1 ? 12 : monthNumber - 1;
      const prevYear = monthNumber === 1 ? yearNumber - 1 : yearNumber;

      // Build all fetch promises
      const rankListPromise = fetchWithTimeout(
        "rankList",
        `rankList?month=${month}&year=${year}`,
        controller.signal
      );
      const teamTargetPromise = fetchWithTimeout(
        "teamTarget",
        `teamTarget?month=${month}&year=${year}`,
        controller.signal
      );
      const mostSoldProductsPromise = fetchWithTimeout(
        "mostSoldProducts",
        `mostSoldProducts?month=${month}&year=${year}`,
        controller.signal
      );
      const yearRankListPromise = fetchWithTimeout(
        "yearRankList",
        `yearRankList?month=${month}&year=${year}`,
        controller.signal
      );
      const bestSellerPromise = fetchWithTimeout(
        "bestSeller",
        `bestSeller?month=${prevMonth}&year=${prevYear}`,
        controller.signal
      );
      const dealsPromise = fetchWithTimeout(
        "deals",
        `getDeals?month=${prevMonth}&year=${prevYear}`,
        controller.signal
      );
      const existantDealsPromise = fetchWithTimeout(
        "existantDeals",
        `getDeals?month=${month}&year=${year}&status=0`,
        controller.signal
      );
      const newDealsPromise = fetchWithTimeout(
        "newDeals",
        `getDeals?month=${month}&year=${year}&status=1`,
        controller.signal
      );

      // Await all
      const results = await Promise.allSettled([
        rankListPromise,
        teamTargetPromise,
        mostSoldProductsPromise,
        yearRankListPromise,
        bestSellerPromise,
        dealsPromise,
        existantDealsPromise,
        newDealsPromise,
      ]);

      let successCount = 0;
      const newCache = { ...cachedData };

      results.forEach((result) => {
        if (
          result.status === "fulfilled" &&
          result.value &&
          result.value.data
        ) {
          successCount++;
          const { type, data } = result.value;
          switch (type) {
            case "rankList":
              if (Array.isArray(data) && data.length > 0) {
                const rankListData = data[0] || [];
                const rankListAmount = data[1] || 0;
                if (isMounted) {
                  setRankList(rankListData);
                  setAmount(rankListAmount);
                }
                const names = rankListData.map((u) => u.name);
                const totals = rankListData.map((u) => u.totalPrice);
                if (isMounted) {
                  setXMonthRankList(names);
                  setYMonthRankList(totals);
                }
                newCache.rankList = rankListData;
                newCache.amount = rankListAmount;
                newCache.xMonthRankList = names;
                newCache.yMonthRankList = totals;
              }
              break;

            case "teamTarget":
              if (isMounted) setTeamTarget(data);
              newCache.teamTarget = data;
              break;

            case "mostSoldProducts":
              if (isMounted) setSoldServices(data);
              newCache.soldServices = data;
              break;

            case "yearRankList":
              if (isMounted) setYearRankList(data);
              newCache.yearRankList = data;
              break;

            case "bestSeller":
              if (isMounted) setBestSeller(data);
              newCache.bestSeller = data;
              break;

            case "deals":
              if (isMounted) setDeals(data);
              newCache.deals = data;
              break;

            case "existantDeals":
              if (isMounted) setExistantDeals(data);
              newCache.existantDeals = data;
              break;

            case "newDeals":
              if (isMounted) setNewDeals(data);
              newCache.newDeals = data;
              break;

            default:
              break;
          }
        }
      });

      // Compute totalPrice = min(teamTarget.amount, amount)
      if (
        newCache.teamTarget.amount !== undefined &&
        newCache.amount !== undefined
      ) {
        const newTotalPrice =
          newCache.teamTarget.amount < newCache.amount
            ? newCache.teamTarget.amount
            : newCache.amount;
        if (isMounted) setTotalPrice(newTotalPrice);
        newCache.totalPrice = newTotalPrice;
      }

      newCache.lastUpdated = Date.now();
      newCache.currentMonth = month;
      newCache.currentYear = year;
      Object.assign(cachedData, newCache);

      if (successCount < 4 && isMounted) {
        // Partial failure → flag error so we can retry automatically
        setHasError(true);
        setErrorReason("Some endpoints failed. Will retry in 20s.");
        setIsLoading(false);
      } else {
        // All good
        fetchAttemptsCount = 0;
        if (isMounted) {
          setIsLoading(false);
          setHasError(false);
          setErrorReason(null);
        }
      }
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      }
      // Log out and schedule retry
      console.error("Fetch error:", error);
      if (isMounted) {
        setHasError(true);
        setErrorReason(error.message || "Unknown error");
        setIsLoading(false);
      }
    } finally {
      isFetchingGlobal = false;
      controller = null;
    }
  };

  // Whenever hasError becomes true, schedule a retry in 20s
  useEffect(() => {
    if (hasError) {
      console.warn("Fetch failed:", errorReason);
      console.warn("Retrying in 20 seconds...");
      const retryTimeout = setTimeout(() => {
        if (isMounted) {
          fetchAll(month, year, true);
          setIsLoading(true);
          setHasError(false);
          setErrorReason(null);
        }
      }, 20000); // 20 seconds

      return () => clearTimeout(retryTimeout);
    }
  }, [hasError, errorReason, month, year]);

  // On mount → start fetching
  useEffect(() => {
    isMounted = true;
    const token = cookies.get("token");
    let loadingTimeoutId = null;

    if (token) {
      if (!isFetchingGlobal) {
        fetchAll(month, year, false);
      }
    } else {
      navigate("/");
    }

    return () => {
      isMounted = false;
      if (loadingTimeoutId) clearTimeout(loadingTimeoutId);
      if (controller) controller.abort();
    };
  }, [month, year]);

  // Token check every 30s
  useEffect(() => {
    let tokenCheckInterval = null;
    const token = cookies.get("token");
    if (token) {
      tokenCheckInterval = setInterval(() => {
        if (!cookies.get("token")) navigate("/");
      }, 30000);
    } else {
      navigate("/");
    }
    return () => {
      if (tokenCheckInterval) clearInterval(tokenCheckInterval);
    };
  }, [cookies, navigate]);

  // Update clock every minute
  useEffect(() => {
    const interval = setInterval(() => {
      if (isMounted) {
        setCurrentTime(getCurrentTime());
      }
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // ────────────────────────────────────────────────────────────────────────────
  //                              UI SUBCOMPONENTS
  // ────────────────────────────────────────────────────────────────────────────

  // Kolom 2 — podium met de top 3 + de rest van de ranglijst + KPI-strip
  const MonthlyRankList = () => {
    const podiumOrder = [1, 0, 2]; // 2e — 1e — 3e
    const rest = rankList.slice(3);
    // De staafhoogte volgt het bedrag t.o.v. de nummer 1. De ondergrens zit
    // in CSS (min-height op .pod-bar), zodat het rangnummer altijd past en de
    // verhouding tussen de bedragen verder eerlijk blijft.
    const topPrice = rankList.length
      ? Math.max(...rankList.slice(0, 3).map((u) => u.totalPrice || 0))
      : 0;
    const barHeight = (price) => {
      if (!topPrice || !price) return 0;
      return Math.round((price / topPrice) * 100);
    };
    // Alleen wisselen als er ook iets te wisselen valt.
    const showRest = rest.length > 0 && rankView === "rest";
    // In de "Andere verkopers"-stand meten de balkjes t.o.v. de hoogste van
    // díe lijst (nummer 4), niet t.o.v. nummer 1 — anders blijft er van elk
    // balkje alleen een spikkel over.
    const restTop = rest.length
      ? Math.max(...rest.map((u) => u.totalPrice || 0))
      : 0;

    return (
      <div className="card rank-column">
        <div className="card-head">
          <h3 className="card-title">
            {showRest ? (
              <>Andere verkopers</>
            ) : (
              <>
                Ranglijst van <span className="hl">{now.toUpperCase()}</span>
              </>
            )}
          </h3>
          <h3 className="card-title">
            {showRest
              ? `nr. 4 t/m ${rankList.length}`
              : `${rankList.length} ${
                  rankList.length === 1 ? "verkoper" : "verkopers"
                }`}
          </h3>
        </div>

        {rankList.length === 0 && (
          <div className="empty-note">Nog geen verkopen in {now}</div>
        )}

        {rankList.length > 0 && !showRest && (
          <div key="podium" className="podium podium--fill">
          {podiumOrder.map((idx, pos) => {
            const user = rankList[idx];
            // Trede zonder verkoper blijft staan — zo is het podium altijd
            // compleet, met een streepje in plaats van een naam.
            if (!user) {
              return (
                <div key={pos} className={`pod pod-${idx + 1} pod-empty`}>
                  <div className="pod-bar" style={{ height: 0 }}>
                    <div className="pod-label">
                      <div className="n">—</div>
                      <div className="v">€0</div>
                    </div>
                    <div className="p">{idx + 1}</div>
                  </div>
                </div>
              );
            }
            return (
              <div
                key={pos}
                className={`pod pod-${idx + 1} animate-fade-in`}
                style={{ animationDelay: `${pos * 0.08}s` }}
              >
                <div
                  className="pod-bar"
                  style={{ height: `${barHeight(user.totalPrice)}%` }}
                >
                  <div className="pod-label">
                    <div className="n">{user.name}</div>
                    <div className="v">€{formatCurrency(user.totalPrice)}</div>
                  </div>
                  <div className="p">{idx + 1}</div>
                </div>
              </div>
            );
          })}
        </div>
        )}

        {showRest && (
          <div key="rest" className="rank-rest">
            {rest.map((user, index) => (
              <div
                key={index}
                className="rank-row animate-fade-in"
                style={{ animationDelay: `${index * 0.06}s` }}
              >
                <div className="no">{index + 4}</div>
                <div className="nm">{user.name}</div>
                <div className="vl">€{formatCurrency(user.totalPrice)}</div>
                <div className="track">
                  <i
                    style={{
                      width: `${
                        restTop
                          ? Math.max(6, (user.totalPrice / restTop) * 100)
                          : 0
                      }%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="kpi-strip">
          <div className="kpi-box">
            <div className="k">Beste verkoper</div>
            <div className="v">
              {bestSeller.name || "—"}
              {bestSeller.name && (
                <span className="medal-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                    <path d="M4 22h16"></path>
                    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
                    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
                    <path d="M12 2v8"></path>
                    <path d="m9 6 3-4 3 4"></path>
                  </svg>
                </span>
              )}
            </div>
            <div className="m">in {previousMonth}</div>
          </div>

          <div className="kpi-box">
            <div className="k">Totaal vorige maand</div>
            <div className="v">€{formatCurrency(deals.totalPrice)}</div>
            <div className="m">
              {deals.totalDeals || 0} deals · gem. €
              {formatCurrency(deals.averagePrice)}
            </div>
          </div>

          <div className="kpi-box">
            <div className="k">Totaal bestaande deals</div>
            <div className="v">€{formatCurrency(existantDeals.totalPrice)}</div>
            <div className="m">
              {existantDeals.totalDeals || 0} deals · gem. €
              {formatCurrency(existantDeals.averagePrice)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Kolom 3 — “Ranglijst afgelopen 12 maanden”
  const YearRankList = () => {
    const maxValue = yearRankList.length
      ? Math.max(...yearRankList.map((u) => u.totalPrice))
      : 0;
    const getMonthLabel = (dateStr) => {
      if (!dateStr) return "";
      const [mm] = dateStr.split("-");
      const months = [
        "Jan",
        "Feb",
        "Mrt",
        "Apr",
        "Mei",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Okt",
        "Nov",
        "Dec",
      ];
      return months[parseInt(mm, 10) - 1];
    };

    return (
      <div className="card">
        <div className="card-head">
          <h3 className="card-title">Ranglijst afgelopen</h3>
          <h3 className="card-title hl">12 maanden</h3>
        </div>
        {yearRankList.length === 0 ? (
          <div className="empty-note">Nog geen historie</div>
        ) : (
          <div className="card-list">
            {yearRankList.map((entry, index) => (
              <div
                key={index}
                className="year-row animate-fade-in"
                style={{ animationDelay: `${index * 0.04}s` }}
              >
                <div className="mo">{getMonthLabel(entry.date)}</div>
                <div className="nm">{entry.name}</div>
                <div className="vl">€{formatCurrency(entry.totalPrice)}</div>
                <div className="track">
                  <i
                    style={{
                      width: `${
                        maxValue
                          ? Math.max(4, (entry.totalPrice / maxValue) * 100)
                          : 0
                      }%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ────────────────────────────────────────────────────────────────────────────
  //                              TARGET‐BEREKENING
  // ────────────────────────────────────────────────────────────────────────────
  let progressPercent = 0,
    remainingAmount = 0,
    daysRemaining = 0,
    perDayNeeded = 0;

  if (!isLoading && !hasError) {
    const salesSoFar = totalPrice || 0;
    const teamGoal = teamTarget.amount || 1;
    progressPercent = Math.min(
      100,
      Math.max(0, Math.floor((salesSoFar / teamGoal) * 100))
    );

    const todayDate = new Date();
    const totalDaysInMonth = dayjs(`${year}-${month}`).daysInMonth();
    const currentDayOfMonth = todayDate.getDate();
    daysRemaining = totalDaysInMonth - currentDayOfMonth;
    remainingAmount = Math.max(0, teamGoal - salesSoFar);
    perDayNeeded =
      daysRemaining > 0
        ? Math.ceil(remainingAmount / daysRemaining)
        : remainingAmount;
  }

  // ────────────────────────────────────────────────────────────────────────────
  //                              RENDER UI
  // ────────────────────────────────────────────────────────────────────────────

  // Spinner zolang we laden of opnieuw proberen
  if (isLoading || hasError) {
    return (
      <ResponsiveContainer>
        <div className="dashboard-container">
          <Navbar />
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        </div>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer>
      <div className="dashboard-container">
        {/* TOPBAR + klok */}
        <Navbar />
        <div className="topbar-clock">
          <span className="tc-time">
            {currentTime.hours}:{currentTime.minutes}
          </span>
          <span className="tc-date">{currentTime.date}</span>
        </div>

        <div className="dash-body">
          {/* ─────── HERO: TEAM TARGET ─────── */}
          <section className="hero">
            {progressPercent >= 100 && <Confetti />}

            <div className="hero-gauge">
              <ArcGauge percent={progressPercent} size={400} stroke={32} />
              <div className="gauge-readout">
                <b>{progressPercent}%</b>
                <span>
                  {progressPercent >= 100 ? "TARGET GEHAALD" : "VAN TARGET"}
                </span>
              </div>
            </div>

            <div className="hero-main">
              <div className="hero-top">
                <h2 className="hero-title">
                  Team target · {now.toUpperCase()}
                </h2>
                <div className="hero-badge">
                  <span className="hero-badge-label">Uitje bij 100%</span>
                  <span className="hero-badge-value">
                    {teamTarget.text || "Nog niet bekend"}
                  </span>
                </div>
              </div>

              <div className="hero-figs">
                <div className="hero-fig">
                  <div className="k">Bedrag</div>
                  <div className="v">€{formatCurrency(amount)}</div>
                </div>
                <div className="hero-fig">
                  <div className="k">Te gaan</div>
                  <div className="v">
                    €
                    {formatCurrency(
                      (teamTarget.amount || 0) - (totalPrice || 0) < 0
                        ? 0
                        : (teamTarget.amount || 0) - (totalPrice || 0)
                    )}
                  </div>
                </div>
                <div className="hero-fig">
                  <div className="k">Target</div>
                  <div className="v">€{formatCurrency(teamTarget.amount)}</div>
                </div>
              </div>

              <div className="hero-bar">
                <div className="track">
                  <i
                    style={{
                      width: `${progressPercent}%`,
                      background: `linear-gradient(90deg, ${heatColor(
                        progressPercent * 0.5
                      )}, ${heatColor(progressPercent)})`,
                      boxShadow: `0 0 ${
                        4 + 8 * (progressPercent / 100)
                      }px ${heatColor(progressPercent, 0.5)}`,
                    }}
                  />
                </div>
                <div className="hero-meta">
                  <span>
                    {daysRemaining > 0 ? daysRemaining : 0} dagen te gaan
                  </span>
                  <span>€{formatCurrency(perDayNeeded)} per dag nodig</span>
                </div>
              </div>
            </div>
          </section>

          {/* ─────── DRIE GELIJKE KOLOMMEN ─────── */}
          <section className="dash-cols">
            <MostSoldServices
              soldServices={soldServices}
              monthLabel={now.toUpperCase()}
            />
            <MonthlyRankList />
            <YearRankList />
          </section>
        </div>
      </div>
    </ResponsiveContainer>
  );
};

export default ResponsiveDashboard;
